import React from "react";

import * as types from "types";
import VylorMark from "../../assets/vylor-mark-black.svg?react";
import Markdown from "../Markdown";
import styles from "./Message.module.scss";
import ToolCallDetails from "./ToolCallDetails";
import ProgressEvent from "./McpEvents/ProgressEvent";
import CopyButton from "./CopyButton";

interface MessageAssistantToolsProps {
  chatThread?: types.ChatThread;
  subMessages: types.AssistantToolsMessage["subMessages"];
  /**
   * Forwarded down to Markdown so MermaidDiagram / VegaLiteChart can defer
   * rendering during streaming.
   */
  isStreaming?: boolean;
  /**
   * Forwarded down to VegaLiteChart for `file://` data URLs.
   */
  getAccessToken?: () => Promise<string>;
  onAskAboutTask?: (prompt: string) => void;
  incognito?: boolean;
}

/**
 * Represents an assistant response with all tool calls and results made during
 * a single chat completion request.
 * Memoized to avoid <ReactMarkdown /> running too often.
 */
export default React.memo(function MessageAssistantTools({
  subMessages,
  isStreaming,
  getAccessToken,
  onAskAboutTask,
  incognito,
}: MessageAssistantToolsProps) {
  const mcpEvents: types.McpEvent[] = subMessages.reduce((acc, curr) => {
    const _mcpEvents = "mcpEvents" in curr ? curr?.mcpEvents ?? [] : [];
    return [...acc, ..._mcpEvents];
  }, [] as types.McpEvent[]);

  // Map progress token to latest progress event.
  // Note: not 100% sure this is necessary.
  // https://gitlab.research.vylor.com/research/ai-pioneers/corteva-chat/corteva-chat-ui/-/merge_requests/156#note_4126364
  const progressTokenToEvent = mcpEvents
    ?.filter((event) => event.event_type === "progress")
    .reduce((acc, curr) => {
      const pEvent = curr as types.McpProgressEvent;
      const progressToken = pEvent.progress_token;
      if (!progressToken) {
        return acc;
      }
      acc[progressToken] = pEvent;
      return acc;
    }, {} as Record<string, types.McpProgressEvent>);

  const progressEvents = Object.values(progressTokenToEvent ?? {}).map((v) => {
    return <ProgressEvent key={v.progress_token} event={v} className="mb2" />;
  });

  let contentForCopyButton: string[] = [];
  const renderedContentList = subMessages.map((msg) => {
    if (types.isAssistantResponseMessage(msg)) {
      contentForCopyButton.push(msg.content);
      return (
        <Markdown
          content={msg.content}
          isStreaming={isStreaming}
          getAccessToken={getAccessToken}
          key={msg.uuid}
        />
      );
    }
    if (types.isToolCallMessage(msg)) {
      return (
        <div key={msg.uuid} className="mb2">
          <ToolCallDetails
            toolName={msg.tool_name}
            toolInput={msg.tool_input}
            toolCallContent={msg.content}
            toolResult={msg.toolResult}
            isError={msg.isError}
            jobId={msg.task_id}
            mcpServer={msg.mcp_server}
            onAskAboutTask={onAskAboutTask}
            incognito={incognito}
          />
        </div>
      );
    }
    return null;
  });

  return (
    <div className={styles.messageContainer}>
      <span className={styles.messageRole}>assistant</span>
      <div className={styles.message}>
        <div className={`${styles.displayPic} assistant`}>
          <VylorMark />
        </div>
        <div className={`${styles.messageContent} assistant`}>
          <CopyButton content={contentForCopyButton.join("")} />
          {renderedContentList}
          {/* Progress indicators rendered after content so they trail the response */}
          {progressEvents && progressEvents.length > 0 && progressEvents}
        </div>
        <div className={styles.avatarSpacer} aria-hidden="true" />
      </div>
    </div>
  );
});
